/* eslint-disable @typescript-eslint/naming-convention */
import { FC, ReactNode, useState, useEffect } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import Tooltip from '@mui/material/Tooltip'
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined'
import GroupAddOutlinedIcon from '@mui/icons-material/GroupAddOutlined'
import AddCircleOutlineOutlinedIcon from '@mui/icons-material/AddCircleOutlineOutlined'
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined'
import TaskAltOutlinedIcon from '@mui/icons-material/TaskAltOutlined'
import NotificationsActiveOutlinedIcon from '@mui/icons-material/NotificationsActiveOutlined'
import UndoOutlinedIcon from '@mui/icons-material/UndoOutlined'
import BlockOutlinedIcon from '@mui/icons-material/BlockOutlined'
import PsychologyAltOutlinedIcon from '@mui/icons-material/PsychologyAltOutlined'
import { useNotification } from 'lib/context/notification.context'

import { useSelectedItems } from '../../../../screens/Platform-administrator/EvaluatedSpace/context/context'

import { superAdminRoutes, platformAdminRoutes, specialAdminRoutes } from 'router/routes'
import {
  Container,
  RowContainer,
  ActionIconButton,
  Row,
  DisabledBoldText,
  ContainerActions,
  ActionCell,
  ActionCellWrapper,
} from './styled'
import { ADMIN, EVALUATE_USER_ROLE, LEAD_ADMIN } from './constants'
import { IAction, IActionSendReminder } from '../store/entities'
import { useTranslation } from 'react-i18next'
import { CircleNumber } from 'views/screens/Platform-administrator/Process/Create/Skills/styled'

interface ICompactAction {
  label: string
  icon: ReactNode
  onClick?: () => void
  disabled?: boolean
  tone?: 'primary' | 'accent' | 'danger'
  to?: string
  state?: any
  onDisabledClick?: () => void
}

const CompactAction = ({
  label,
  icon,
  onClick,
  disabled = false,
  tone = 'primary',
  to,
  state,
  onDisabledClick,
}: ICompactAction) => {
  const handleButtonClick = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation()
    if (disabled) {
      event.preventDefault()
      onDisabledClick?.()
      return
    }
    onClick?.()
  }

  const actionNode = to ? (
    <Link
      to={to}
      state={state}
      onClick={handleButtonClick}
      style={{ display: 'inline-flex', textDecoration: 'none' }}
      aria-label={label}
    >
      <ActionIconButton as="span" tone={tone} isDisabled={disabled}>
        {icon}
      </ActionIconButton>
    </Link>
  ) : (
    <ActionIconButton type="button" tone={tone} isDisabled={disabled} onClick={handleButtonClick} aria-label={label}>
      {icon}
    </ActionIconButton>
  )

  return (
    <Tooltip title={label} arrow placement="top">
      <span style={{ display: 'inline-flex' }}>{actionNode}</span>
    </Tooltip>
  )
}

interface IActionEdit {
  id: string | number
  level_id: number
}

/**
 * @description Action to edit a company
 * @param item
 * */
export const ActionEditCompany = (item: IActionEdit) => {
  const navigate = useNavigate()
  const { id } = item
  return (
    <Container>
      <CompactAction
        label="Ver más"
        tone="accent"
        icon={<VisibilityOutlinedIcon sx={{ fontSize: 18 }} />}
        onClick={() => navigate(`${superAdminRoutes.CompanyInfo}?companyId=${id}`)}
      />
      <CompactAction
        label="Editar"
        icon={<EditOutlinedIcon sx={{ fontSize: 18 }} />}
        onClick={() => navigate(`${superAdminRoutes.UpdateCompany}?companyId=${id}`)}
      />
    </Container>
  )
}

/**
 * @description Action to edit a skill
 */
export const ActionEditSkill = (item: IActionEdit) => {
  const { id, level_id: LevelId } = item
  return (
    <Container>
      <CompactAction
        label="Editar"
        to={`/${specialAdminRoutes.UpdateSkill}?id=${id}&level=${LevelId}`}
        icon={<EditOutlinedIcon sx={{ fontSize: 18 }} />}
      />
    </Container>
  )
}

export const ActionEditSkillQuestion = (item: IActionEdit) => {
  const { id } = item
  return (
    <Container>
      <CompactAction
        label="Editar"
        to={`${superAdminRoutes.UpdateSkillQuestion}?id=${id}`}
        icon={<EditOutlinedIcon sx={{ fontSize: 18 }} />}
      />
    </Container>
  )
}

interface IActionEditQuestionnaire {
  id: string | number
  skill: string // name of the questionnaire
  can_edit?: boolean
  onNoEditAccess?: () => void
}
/**
 * @description Action to edit a questionnaire
 */
export const ActionEditQuestionnaire = (props: IActionEditQuestionnaire) => {
  const { id, skill, can_edit: canEdit = true } = props

  return (
    <CompactAction
      label={canEdit ? 'Editar' : 'Ver'}
      to={superAdminRoutes.SettingQuestionaire}
      state={{ questionnaireId: id, questionnaireName: skill }}
      icon={canEdit ? <EditOutlinedIcon sx={{ fontSize: 18 }} /> : <VisibilityOutlinedIcon sx={{ fontSize: 18 }} />}
      tone={canEdit ? 'primary' : 'accent'}
    />
  )
}

interface IActionAddTalent {
  onAddTalent: (props: any) => void
  onViewTalent: (props: any) => void
  idTalentsSelected: number[]
  id: number
}

/**
 * @description Action add talent
 */
export const ActionAddTalent = (props: IActionAddTalent) => {
  const { onAddTalent, onViewTalent, idTalentsSelected } = props

  function isTalentSelected(idTalent: number) {
    return idTalentsSelected.includes(idTalent)
  }

  return (
    <Row>
      <CompactAction label="Ver" tone="accent" icon={<VisibilityOutlinedIcon sx={{ fontSize: 18 }} />} onClick={() => onViewTalent(props)} />
      <CompactAction
        label="Agregar"
        icon={<AddCircleOutlineOutlinedIcon sx={{ fontSize: 18 }} />}
        disabled={isTalentSelected(props.id) || idTalentsSelected.length >= 3}
        onClick={() => onAddTalent(props)}
      />
    </Row>
  )
}

interface IActionDesassociateTalent {
  onDeleteTalent: (props: any) => void
  onViewTalent: () => void
}

/**
 * @description Action to desassociate a talent of an development plan
 */
export const ActionDesassociateTalent = (props: IActionDesassociateTalent) => {
  const { onDeleteTalent, onViewTalent } = props
  return (
    <Row>
      <CompactAction label="Ver" tone="accent" icon={<VisibilityOutlinedIcon sx={{ fontSize: 18 }} />} onClick={onViewTalent} />
      <CompactAction
        label="Eliminar"
        tone="danger"
        icon={<DeleteOutlineOutlinedIcon sx={{ fontSize: 18 }} />}
        onClick={() => onDeleteTalent(props)}
      />
    </Row>
  )
}

/**
 * @description Action to view info of user by company
 * */
export const ActionViewInfoUserByCompany = (props: any) => {
  const navigate = useNavigate()

  const { active, complete, edit, ...rest } = props

  const user_name = rest?.first_name + ' ' + rest?.last_name

  const body = { user_name, ...rest }

  return (
    <CompactAction
      label="Ver"
      tone="accent"
      icon={<VisibilityOutlinedIcon sx={{ fontSize: 18 }} />}
      onClick={() => {
        navigate(superAdminRoutes.InfoEvaluatedUserByCompany, { state: { userData: body } })
      }}
    />
  )
}

interface IActionEditUser {
  onSelectUserId: (id: number) => void
  id: number
  role: string
  company_id: number
  userId: number
  process_ids: number[]
  first_name: string
}

/**
 * @description Action to view o edit an user
 */
export const ActionViewEditUser = (props: IActionEditUser) => {
  const { onSelectUserId, userId } = props

  const viewByUser =
    props?.role === ADMIN.platform || props?.role === LEAD_ADMIN
      ? superAdminRoutes.PlatformAdminsByCompany
      : props?.role === EVALUATE_USER_ROLE
      ? superAdminRoutes.InfoEvaluatedUserByCompany
      : superAdminRoutes.InfoEvaluatedUserByCompany

  const stateUser =
    props?.role === ADMIN.platform || props?.role === LEAD_ADMIN
      ? { companyId: props.company_id }
      : props?.role === EVALUATE_USER_ROLE
      ? {
          userData: {
            companyId: props.company_id,
            id: props.userId,
            process_id: props.process_ids[0],
            user_name: props.first_name,
          },
        }
      : null

  return (
    <Row>
      {props.role === ADMIN.platform || props.role === LEAD_ADMIN ? (
        <></>
      ) : (
        <CompactAction label="Ver" tone="accent" to={viewByUser || '/'} state={stateUser} icon={<VisibilityOutlinedIcon sx={{ fontSize: 18 }} />} />
      )}
      {(props.role === ADMIN.platform || props.role === LEAD_ADMIN) && (
        <CompactAction
          label="Editar"
          icon={<EditOutlinedIcon sx={{ fontSize: 18 }} />}
          onClick={() => {
            if (onSelectUserId) onSelectUserId(userId)
          }}
        />
      )}
    </Row>
  )
}

// :::::::::: PLATFORM ADMIN :::::::::::::: //

/**
 * @description Action to view or download a report of a space of evaluated users
 */
export const ActionViewDownloadReportSpaceUserEvaluated = (
  props: IAction & { complete: number; handleDownload: (id: number) => void }
) => {
  const selectedItems = useSelectedItems()
  const isDisabled = selectedItems.size > 1
  // const isDisabledCompleted = props.complete === 0

  return (
    <Row>
      <CompactAction
        label="Ver"
        tone="accent"
        disabled={isDisabled}
        to={platformAdminRoutes.DetailEvaluatedUser}
        state={{ userId: props.id, processId: props.process, completed: props.complete }}
        icon={<VisibilityOutlinedIcon sx={{ fontSize: 18 }} />}
      />
    </Row>
  )
}

interface IActionAddSkill {
  handleAddSkill: (props: any) => void
  onEditSkill: (props: any) => void
  skillsSelected: number[]
  id: number
}

/**
 * @description Action add skill to development plan
 */
export const ActionAddSkillToDevelop = (props: IActionAddSkill) => {
  const { handleAddSkill, onEditSkill, skillsSelected } = props

  function isSkillSelected(idSkill: number) {
    if (!skillsSelected) return false
    const idSkills = skillsSelected.map((skill: any) => skill.id)
    return idSkills.includes(idSkill)
  }

  return (
    <Row>
      <CompactAction label="Ver" tone="accent" icon={<VisibilityOutlinedIcon sx={{ fontSize: 18 }} />} onClick={() => onEditSkill(props)} />
      <CompactAction
        label="Agregar"
        icon={<AddCircleOutlineOutlinedIcon sx={{ fontSize: 18 }} />}
        disabled={isSkillSelected(props.id) || skillsSelected.length >= 3}
        onClick={() => handleAddSkill(props)}
      />
    </Row>
  )
}

interface IActionAddSkill {
  onDeleteSkill: (props: any) => void
  onViewSkill: (props: any) => void
  skillsSelected: number[]
  id: number
  weight: number
}
/**
 * @description Action delete skill to development plan
 */
export const ActionDeleteSkillToDevelop = (props: IActionAddSkill) => {
  const { onDeleteSkill, onEditSkill } = props

  return (
    <Row>
      <CompactAction label="Ver" tone="accent" icon={<VisibilityOutlinedIcon sx={{ fontSize: 18 }} />} onClick={() => onEditSkill(props)} />
      <CompactAction
        label="Eliminar"
        tone="danger"
        icon={<DeleteOutlineOutlinedIcon sx={{ fontSize: 18 }} />}
        onClick={() => onDeleteSkill(props)}
      />
    </Row>
  )
}
interface IActionAddTalent {
  onDeleteTalent: (props: any) => void
  onViewTalent: (props: any) => void
  talentsSelected: number[]
  id: number
}

/**
 * @description Action des-select talent to development plan
 */
export const ActionDeleteTalentToDevelop = (props: IActionAddTalent) => {
  const { onDeleteTalent, onViewTalent } = props

  return (
    <Row>
      <CompactAction label="Ver" tone="accent" icon={<VisibilityOutlinedIcon sx={{ fontSize: 18 }} />} onClick={() => onViewTalent(props)} />
      <CompactAction
        label="Eliminar"
        tone="danger"
        icon={<DeleteOutlineOutlinedIcon sx={{ fontSize: 18 }} />}
        onClick={() => onDeleteTalent(props)}
      />
    </Row>
  )
}

interface IActionEditProcess {
  onFinishProcess: (id: number) => void
  id: number
}
/**
 * @description Action edit draft process
 */
export const ActionEditDraftProcess = (item: IActionEditProcess) => {
  const { onFinishProcess, id } = item

  return (
    <>
      <CompactAction label="Terminar" icon={<TaskAltOutlinedIcon sx={{ fontSize: 18 }} />} onClick={() => onFinishProcess(id)} />
    </>
  )
}

type IDownloadProcess = {
  id: number
  users: number[]
  name: string
  competencesTest: boolean
  betesaTest: boolean
  test_360: boolean
}

interface IActionViewDownloadAddUsers {
  onAddUsers: (id: number) => void
  onViewProcess: (id: number) => void
  onDownloadProcess: (data: IDownloadProcess) => void
  id: number
  users: number[]
  name: string
  competences_test: boolean
  betesa_test: boolean
  test_360: boolean
  isDisabled?: boolean
}

/**
 * @description Action to view, download and add users to process
 */
export const ActionViewDownloadAddUsers = (props: IActionViewDownloadAddUsers) => {
  const {
    onAddUsers,
    onViewProcess,
    onDownloadProcess,
    id,
    users,
    name,
    competences_test: competencesTest,
    betesa_test: betesaTest,
    test_360,
    isDisabled,
  } = props

  return (
    <ActionCellWrapper>
      <ActionCell>
        <CompactAction label="Ver" tone="accent" icon={<VisibilityOutlinedIcon sx={{ fontSize: 18 }} />} onClick={() => onViewProcess(id)} />
      </ActionCell>
      <ActionCell>
        <CompactAction
          label="Descargar resumen"
          icon={<DownloadOutlinedIcon sx={{ fontSize: 18 }} />}
          disabled={isDisabled}
          onClick={() => onDownloadProcess({ id, users, name, betesaTest, competencesTest, test_360 })}
        />
      </ActionCell>
      <ActionCell>
        <CompactAction label="Administrar usuarios" icon={<GroupAddOutlinedIcon sx={{ fontSize: 18 }} />} onClick={() => onAddUsers(id)} />
      </ActionCell>
    </ActionCellWrapper>
  )
}

interface IActionViewDownloadReportProcessCreated {
  listIdUsers: Set<number>
  onDownloadReport: (id: number) => void
  id: number
  processId: number
}

export const ActionViewDownloadReportProcessCreated = (props: IActionViewDownloadReportProcessCreated) => {
  const {
    // listIdUsers,
    id,
    // onDownloadReport,
    processId,
  } = props
  const navigate = useNavigate()

  return (
    <Container>
      <CompactAction
        label="Ver"
        tone="accent"
        icon={<VisibilityOutlinedIcon sx={{ fontSize: 18 }} />}
        onClick={() => navigate(platformAdminRoutes.DetailEvaluatedUser, { state: { userId: id, processId: processId } })}
      />
    </Container>
  )
}

type Users = {
  userId: number
  processId: number
}

interface IReminderButtonProps {
  onSendReminder: (email: string) => void
  email: string
  testReminder: null | string
  reminderSent: any
  onWarning: () => void
  disabled: boolean
}

export const ReminderButton = ({ onSendReminder, email, testReminder, onWarning, disabled }: IReminderButtonProps) => {
  const [isReminderSent, setIsReminderSent] = useState(false)
  const [isDisabled, setIsDisabled] = useState(false)

  useEffect(() => {
    if (testReminder === null) {
      setIsReminderSent(false)
      setIsDisabled(false)
      return
    }

    const testReminderDate = new Date(testReminder)
    const currentDate = new Date()
    const timeDifference = currentDate.getTime() - testReminderDate.getTime()
    const isAtLeast24HoursAfter = timeDifference >= 24 * 60 * 60 * 1000
    setIsDisabled(!isAtLeast24HoursAfter)
    setIsReminderSent(isAtLeast24HoursAfter)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleSendReminderClick = () => {
    if (isReminderSent && isDisabled) {
      onWarning()
    } else {
      onSendReminder(email)
      setIsReminderSent(true)
      setIsDisabled(true)
    }
  }

  return (
    <CompactAction
      label="Enviar recordatorio"
      tone="accent"
      icon={<NotificationsActiveOutlinedIcon sx={{ fontSize: 18 }} />}
      disabled={isDisabled || disabled}
      onClick={handleSendReminderClick}
      onDisabledClick={isReminderSent && isDisabled ? onWarning : undefined}
    />
  )
}
interface IActionSendReminderAndReturnCreditToInventory {
  onViewUser: (props: { userId: number; processId: number }) => void
  onSendReminder: (email: string) => void
  onReturnCreditToInventory: (props: Users) => void
  email: string
  userId: number
  processId: number
  testReminder: null | string
  usersWithReminderState: any
  disabled: boolean
}

export const ActionSendReminderAndReturnCreditToInventory = (props: IActionSendReminderAndReturnCreditToInventory) => {
  const {
    onViewUser,
    onReturnCreditToInventory,
    onSendReminder,
    email,
    userId,
    processId,
    testReminder,
    usersWithReminderState,
    disabled,
  } = props
  const dataUsers = usersWithReminderState?.usersWithReminderState
  const userWithReminder = dataUsers.find((user: { email: string }) => user.email === email)
  const { getWarning } = useNotification()

  const showWarning = () => {
    getWarning('Recordatorio enviado anteriormente')
  }

  return (
    <Container
      style={{
        flexDirection: 'row',
        alignItems: 'center',
        gap: '8px',
      }}
    >
      <CompactAction
        label="Ver"
        tone="accent"
        icon={<VisibilityOutlinedIcon sx={{ fontSize: 18 }} />}
        onClick={() => onViewUser({ userId, processId })}
      />
      <RowContainer style={{ padding: 0, background: 'transparent', transform: 'none' }}>
        <ReminderButton
          onSendReminder={onSendReminder}
          email={email}
          testReminder={testReminder}
          reminderSent={userWithReminder?.isReminderSent}
          onWarning={showWarning}
          disabled={disabled}
        />
      </RowContainer>
      <CompactAction
        label="Devolver al inventario"
        icon={<UndoOutlinedIcon sx={{ fontSize: 18 }} />}
        disabled={disabled}
        onClick={() => onReturnCreditToInventory({ userId, processId })}
      />
    </Container>
  )
}

export const ActionSendReminder = (props: IActionSendReminder) => {
  const { onSendReminder, email } = props

  return (
    <Container>
      <CompactAction
        label="Enviar recordatorio"
        tone="accent"
        icon={<NotificationsActiveOutlinedIcon sx={{ fontSize: 18 }} />}
        onClick={() => onSendReminder(email)}
      />
    </Container>
  )
}

/**
 * @description Action add skills in create process
 */

export const ActionAddSkillCreateProcess = (props: IActionAddSkill) => {
  const { t } = useTranslation()
  const { handleAddSkill, onViewSkill, skillsSelected } = props
  const { getWarning } = useNotification()

  function isSkillSelected(idSkill: number) {
    if (!skillsSelected) return false
    const idSkills = skillsSelected.map((skill: any) => skill.id)
    return idSkills.includes(idSkill)
  }

  return (
    <Row>
      <CompactAction label="Ver" icon={<VisibilityOutlinedIcon sx={{ fontSize: 18 }} />} onClick={() => onViewSkill(props)} />
      <CompactAction
        label={isSkillSelected(props.id) ? 'Agregada' : 'Agregar'}
        icon={<AddCircleOutlineOutlinedIcon sx={{ fontSize: 18 }} />}
        disabled={isSkillSelected(props.id) || skillsSelected.length >= 12}
        onClick={() => handleAddSkill(props)}
        onDisabledClick={() => {
          if (skillsSelected.length >= 12) {
            getWarning(t('global.notifications.addSkillsErrorMoreThanMessage', { number: 12 }))
          }

          if (isSkillSelected(props.id) && skillsSelected.length < 12) {
            getWarning(t('global.notifications.notAddSameSkillErrorMessage'))
          }
        }}
      />
    </Row>
  )
}

/**
 * @description Action delete skill in create process
 */
export const ActionDeleteSkillCreateProcess = (props: IActionAddSkill) => {
  const { onDeleteSkill } = props
  return (
    <Row>
      <CompactAction
        label="Eliminar"
        tone="danger"
        icon={<DeleteOutlineOutlinedIcon sx={{ fontSize: 18 }} />}
        onClick={() => onDeleteSkill(props)}
      />
    </Row>
  )
}

interface IActionEditAndDeleteSkillSelected {
  onDeleteSkill: (props: any) => void
  onEditSkill: (props: any) => void
}

/**
 * @description Action delete and edit betesa skill selected in create process
 */
export const ActionEditAndDeleteBetesaSkillSelected = (props: IActionEditAndDeleteSkillSelected) => {
  const { onDeleteSkill, onEditSkill } = props
  return (
    <Row>
      <CompactAction label="Editar" icon={<EditOutlinedIcon sx={{ fontSize: 18 }} />} onClick={() => onEditSkill(props)} />
      <CompactAction
        label="Eliminar"
        tone="danger"
        icon={<DeleteOutlineOutlinedIcon sx={{ fontSize: 18 }} />}
        onClick={() => onDeleteSkill(props)}
      />
    </Row>
  )
}

interface IActionRequestDeletePlatformAdmin {
  onRequestDeletePlatformAdmin: (userId: number) => void
  onReactivatePlatformAdmin?: (userId: number) => void
  active: boolean
  userId: number
  status_id?: number
}

/**
 * @description Action to suspend or reactivate a platform admin
 */
export const ActionRequestSuspensionPlatformAdmin = (props: IActionRequestDeletePlatformAdmin) => {
  const { onRequestDeletePlatformAdmin, onReactivatePlatformAdmin, active, userId, status_id: statusId } = props

  return (
    <Row>
      {active ? (
        <CompactAction
          label="Suspender"
          tone="danger"
          icon={<BlockOutlinedIcon sx={{ fontSize: 18 }} />}
          onClick={() => onRequestDeletePlatformAdmin(userId)}
        />
      ) : statusId === 2 ? (
        <CompactAction
          label="Reactivar"
          icon={<AddCircleOutlineOutlinedIcon sx={{ fontSize: 18 }} />}
          onClick={() => onReactivatePlatformAdmin?.(userId)}
        />
      ) : (
        <RowContainer width="100%">
          <DisabledBoldText>Usuario sin activar</DisabledBoldText>
        </RowContainer>
      )}
    </Row>
  )
}

/**
 * @description Action to supend an user admin platform
 */

interface IActionSuspendUserAdminPlatform {
  onSuspendUserAdminPlatform: (props: any) => void
  onActivateUserAdminPlatform: (props: any) => void
  active: boolean
  user_id: number
  request_status: 'pending' | 'approved' | 'rejected'
}

export const ActionSuspendUserAdminPlatform = (props: IActionSuspendUserAdminPlatform) => {
  const { onSuspendUserAdminPlatform, onActivateUserAdminPlatform, request_status: RequestStatus } = props

  return (
    <Row>
      {RequestStatus === 'pending' ? (
        <CompactAction
          label="Suspender"
          tone="danger"
          icon={<BlockOutlinedIcon sx={{ fontSize: 18 }} />}
          onClick={() => onSuspendUserAdminPlatform(props)}
        />
      ) : (
        <CompactAction
          label="Reactivar usuario"
          icon={<AddCircleOutlineOutlinedIcon sx={{ fontSize: 18 }} />}
          onClick={() => onActivateUserAdminPlatform(props)}
        />
      )}
    </Row>
  )
}

interface InterpretationButtonProps {
  betesa_result_id: number
  company_name: string
  user_name: string
}

export const InterpretationButton: FC<InterpretationButtonProps> = ({ betesa_result_id }) => {
  return (
    <ContainerActions>
      <CompactAction
        label="Interpretar"
        tone="accent"
        to={`/interpretation/detail?id=${betesa_result_id}`}
        icon={<PsychologyAltOutlinedIcon sx={{ fontSize: 18 }} />}
      />
    </ContainerActions>
  )
}

export const ActionAiSkillsCreateProcess = (props: IActionAddSkill) => {
  const { onDeleteSkill, onViewSkill, weight } = props

  return (
    <Row>
      <RowContainer>
        <CircleNumber>{weight}</CircleNumber>
      </RowContainer>
      <CompactAction label="Ver" icon={<VisibilityOutlinedIcon sx={{ fontSize: 18 }} />} onClick={() => onViewSkill(props)} />
      <CompactAction
        label="Eliminar"
        tone="danger"
        icon={<DeleteOutlineOutlinedIcon sx={{ fontSize: 18 }} />}
        onClick={() => onDeleteSkill(props)}
      />
    </Row>
  )
}

export default InterpretationButton
